/**
 * Keyboard shortcuts. Single keys only act when you are not typing in a field, so they never get in
 * the way of writing (WCAG 2.1.4); Ctrl+K or ⌘K works everywhere.
 *   Ctrl+K, ⌘K or /   search and commands       ?   this list
 *   g then a letter    go to a page               n   add a lead
 *   [                  collapse or expand the sidebar
 */
import { h } from "./dom.ts";
import { icon } from "./icons.ts";
import { PAGES } from "./nav.ts";
import { prefs, setPrefs } from "./prefs.ts";
import { langAttrs, t } from "./i18n.ts";

export const shortcutList = (): { keys: string[]; label: string }[] => [
  { keys: ["Ctrl", "K"], label: `${t("Search and commands")} (⌘K, Mac)` },
  { keys: ["/"], label: t("Search and commands") },
  { keys: ["?"], label: t("Show these shortcuts") },
  { keys: ["N"], label: t("Add a lead or prospect") },
  { keys: ["["], label: t("Collapse or expand the sidebar") },
  ...PAGES.map((p) => ({ keys: ["G", p.go.toUpperCase()], label: t("Go to {x}", { x: t(p.label) }) })),
  { keys: ["Esc"], label: t("Close a dialog, the palette or the menu") },
];

export function keysList(): HTMLElement {
  return h("dl", { class: "keys", ...langAttrs() }, shortcutList().flatMap((s) => [h("dt", null, s.label), h("dd", null, s.keys.map((k) => h("kbd", null, k)))]));
}

export function showShortcuts(): void {
  if (document.querySelector("dialog.modal[open]")) return;
  const close = h("button", { type: "button", class: "icon-btn", "aria-label": "Close" }, icon("close"));
  const d = h("dialog", { class: "modal", "aria-labelledby": "keys-title", ...langAttrs() },
    h("div", { class: "modal-head" }, icon("keyboard"), h("h2", { id: "keys-title" }, t("Keyboard shortcuts")), close),
    h("div", { class: "modal-body" }, h("p", { class: "muted small" }, t("Single keys work when you are not typing in a box.")), keysList()),
  );
  close.addEventListener("click", () => d.close());
  d.addEventListener("click", (e) => { if (e.target === d) d.close(); });
  d.addEventListener("close", () => d.remove());
  document.body.append(d);
  d.showModal();
  close.focus();
}

const typing = (t: EventTarget | null): boolean => {
  const el = t as HTMLElement | null;
  if (!el) return false;
  return el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName);
};

export function installShortcuts(actions: { palette: () => void; newLead: () => void }): void {
  let pendingG = 0;
  document.addEventListener("keydown", (e) => {
    if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === "k") {
      e.preventDefault();
      actions.palette();
      return;
    }
    if (e.ctrlKey || e.metaKey || e.altKey || typing(e.target) || document.querySelector("dialog[open]")) return;
    if (pendingG && Date.now() - pendingG < 1200) {
      pendingG = 0;
      const page = PAGES.find((p) => p.go === e.key.toLowerCase());
      if (page) {
        e.preventDefault();
        location.hash = page.href;
      }
      return;
    }
    switch (e.key) {
      case "/": e.preventDefault(); actions.palette(); break;
      case "?": e.preventDefault(); showShortcuts(); break;
      case "g": case "G": pendingG = Date.now(); break;
      case "n": case "N": e.preventDefault(); actions.newLead(); break;
      case "[": setPrefs({ sidebarCollapsed: !prefs().sidebarCollapsed }); break;
    }
  });
}
